import { useEffect, useState } from "react";
import type { DataTableState, TableSort } from "@h2j-data-team/ui";

// The analysis spans more than one storage format (QVD, Parquet) and the API
// carries no format field, so the extension on the row's path is the signal.
export function fileFormatLabel(pathOrName: string | undefined): string | null {
  if (!pathOrName) return null;
  // Extension of the BASE name, mirroring the Go extOf/baseName pair: a dot in a
  // directory ("data.v2/vendas") is not an extension, and both separators split.
  const base = pathOrName.slice(
    Math.max(pathOrName.lastIndexOf("/"), pathOrName.lastIndexOf("\\")) + 1,
  );
  const dot = base.lastIndexOf(".");
  if (dot < 0 || dot === base.length - 1) return null;
  const ext = base.slice(dot + 1).toLowerCase();
  return ext === "parquet" ? "Parquet" : ext.toUpperCase();
}

// Debounce the search box so each keystroke doesn't refetch the server query.
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Page size of a DataTable that shows every row it is given (no pagination). */
export const ALL_ROWS = Number.MAX_SAFE_INTEGER;

/** State of a DataTable whose order, search and filters never change. */
export const STATIC_TABLE_STATE: DataTableState = {
  page: 1,
  pageSize: ALL_ROWS,
  query: "",
  filters: {},
  selection: [],
};

export const noop = () => {};

/**
 * The inventory lists are ordered by the server: by size (largest first) or by
 * name (A–Z). Each column head picks one of the two and the direction comes with
 * it, so a head cannot reverse its own order.
 */
export const SERVER_SORT_DIRECTION: Record<"size" | "name", TableSort["direction"]> = {
  size: "desc",
  name: "asc",
};
